import { useEffect, useState } from 'react';
import { FileX, ChevronDown, ChevronUp } from 'lucide-react';
import { RepairActionCard } from './RepairActionCard';
import { useReconciliation } from '../useReconciliation';
import { reconciliationApi } from '../reconciliation-api';
import type { RepairActionResult } from '../../invoice-repair/types';

export function StepMissing() {
  const { diagnostic, dateFrom, dateTo, storeId, updateStepStatus, addRepairLog, isExecuting, setIsExecuting } = useReconciliation();
  const [showNotQualifying, setShowNotQualifying] = useState(false);

  const stats = diagnostic?.stats;
  const notQualifying = diagnostic?.notQualifyingDetails || [];

  useEffect(() => {
    if (!stats) {
      updateStepStatus('missing', 'idle');
      return;
    }
    if (stats.missingInvoices === 0 && stats.codReturnedWithInvoice === 0) {
      updateStepStatus('missing', 'done', 'Brak brakujących faktur');
    } else {
      updateStepStatus('missing', 'warning', `${stats.missingInvoices} brakujących, ${stats.codReturnedWithInvoice} COD zwrotów`);
    }
  }, [stats]);

  if (!diagnostic) {
    return <div className="text-center py-12 text-text-secondary text-sm">Uruchom diagnostykę w kroku 1.</div>;
  }

  const logAction = (action: string, description: string, result: RepairActionResult) => {
    addRepairLog({
      action,
      description,
      changes: (result.invoices || result.results || []).slice(0, 20).map((inv: any) => ({
        invoiceId: inv.id || '',
        field: 'created',
        oldValue: 'brak',
        newValue: inv.invoiceNumber || inv.number || 'utworzono',
        label: inv.orderNumber || '',
      })),
      result: result.failCount && result.failCount > 0 ? 'partial' : 'success',
    });
  };

  return (
    <div className="space-y-4">
      {/* Status */}
      {stats && stats.missingInvoices === 0 && stats.codReturnedWithInvoice === 0 ? (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-success/5 border border-success/20 text-sm text-success">
          <FileX className="w-4 h-4" />
          Wszystkie kwalifikujące się zamówienia mają fakturę
        </div>
      ) : (
        <>
          {/* Missing invoices */}
          {stats && stats.missingInvoices > 0 && (
            <RepairActionCard
              title="Brakujące faktury"
              description={`${stats.missingInvoices} zamówień spełnia kryteria fakturowania, ale nie ma faktury`}
              count={stats.missingInvoices}
              severity="error"
              disabled={isExecuting}
              onPreview={() => reconciliationApi.bulkCreateMissing(true, dateFrom, dateTo, storeId)}
              onExecute={async () => {
                setIsExecuting(true);
                try {
                  const res = await reconciliationApi.bulkCreateMissing(false, dateFrom, dateTo, storeId);
                  logAction('bulk-create-missing', `Wystawiono ${res.count} brakujących faktur`, res);
                  return res;
                } finally { setIsExecuting(false); }
              }}
            />
          )}

          {/* COD returned with invoice */}
          {stats && stats.codReturnedWithInvoice > 0 && (
            <RepairActionCard
              title="COD ze zwróconymi przesyłkami"
              description={`${stats.codReturnedWithInvoice} zamówień COD z fakturą, ale przesyłka zwrócona`}
              count={stats.codReturnedWithInvoice}
              severity="warning"
              destructive
              disabled={isExecuting}
              onPreview={() => reconciliationApi.deleteCodReturned(true, dateFrom, dateTo, storeId)}
              onExecute={async () => {
                setIsExecuting(true);
                try {
                  const res = await reconciliationApi.deleteCodReturned(false, dateFrom, dateTo, storeId);
                  logAction('delete-cod-returned', `Usunięto ${res.count} faktur COD zwrotów`, res);
                  return res;
                } finally { setIsExecuting(false); }
              }}
            />
          )}
        </>
      )}

      {/* Not qualifying section */}
      {notQualifying.length > 0 && (
        <div className="rounded-xl border border-border-subtle bg-surface-raised/30">
          <button
            onClick={() => setShowNotQualifying(!showNotQualifying)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-text-secondary hover:text-text-secondary"
          >
            <span>Niekwalifikujące się zamówienia ({stats?.notQualifying ?? 0})</span>
            {showNotQualifying ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showNotQualifying && (
            <div className="px-4 pb-3 space-y-1 max-h-60 overflow-y-auto">
              {notQualifying.map((item: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-border-subtle last:border-0">
                  <span className="text-text-secondary">{item.label}</span>
                  {item.badge && (
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      item.badgeColor === 'red' ? 'bg-danger/20 text-danger' :
                      item.badgeColor === 'yellow' ? 'bg-warning/20 text-warning' :
                      item.badgeColor === 'green' ? 'bg-success/20 text-success' :
                      'bg-gray-500/20 text-text-secondary'
                    }`}>{item.badge}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
